import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface Trail {
  id: string;
  name: string;
  name_short?: string;
  length_miles?: number;
  created_at: string;
  updated_at: string;
}

export interface TrailSegment {
  id: string;
  trail_id: string;
  trail_name?: string;
  trail_name_short?: string;
  segment_order?: number;
  length_miles?: number;
  difficulty?: 'easy' | 'moderate' | 'hard' | 'extreme';
  surface_type?: string;
  features?: string[];
  geometry: any;
  mvum_symbol?: string;
  maintenance_level?: string;
  allowed_vehicles?: string[];
  forest_name?: string;
  district_name?: string;
  jurisdiction?: string;
  existence_status?: string;
  seasonal_restrictions?: string;
}

export interface TrailFilters {
  search?: string;
  difficulty?: 'all' | 'easy' | 'moderate' | 'hard' | 'extreme';
  vehicleType?: string;
  region?: string;
}

export const useTrails = (filters: TrailFilters = {}) => {
  const [trails, setTrails] = useState<Trail[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTrails = async () => {
      try {
        setIsLoading(true);
        setError(null);

        let query = supabase
          .from('trails')
          .select('id, name, name_short, length_miles, created_at, updated_at');

        // Apply filters
        if (filters.search) {
          query = query.ilike('name', `%${filters.search}%`);
        }

        // For difficulty filtering, we need to join with trail_segments
        if (filters.difficulty && filters.difficulty !== 'all') {
          // This will be a more complex query later
          // For now, we'll filter client-side or create a database view
        }

        // Order by name
        query = query.order('name');

        const { data, error: fetchError } = await query;

        if (fetchError) {
          throw fetchError;
        }

        setTrails(data || []);
      } catch (err) {
        console.error('Error fetching trails:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch trails');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTrails();
  }, [filters.search, filters.difficulty, filters.region]);

  return { trails, isLoading, error };
};

export const useTrailById = (trailId: string | undefined) => {
  const [trail, setTrail] = useState<Trail | null>(null);
  const [segments, setSegments] = useState<TrailSegment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!trailId) {
      setIsLoading(false);
      return;
    }

    const fetchTrailDetails = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Fetch trail
        const { data: trailData, error: trailError } = await supabase
          .from('trails')
          .select('*')
          .eq('id', trailId)
          .maybeSingle();

        if (trailError) {
          throw trailError;
        }

        setTrail(trailData);

        // Fetch segments
        if (trailData) {
          const { data: segmentsData, error: segmentsError } = await supabase
            .from('trail_segments')
            .select('*')
            .eq('trail_id', trailId)
            .order('segment_order');

          if (segmentsError) {
            throw segmentsError;
          }

          setSegments(segmentsData || []);
        }
      } catch (err) {
        console.error('Error fetching trail details:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch trail details');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTrailDetails();
  }, [trailId]);

  return { trail, segments, isLoading, error };
};

export const useTrailSegments = (bbox?: { north: number; south: number; east: number; west: number; zoom: number }) => {
  const [segments, setSegments] = useState<TrailSegment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSegments = async () => {
      try {
        setIsLoading(true);
        setError(null);

        let query = supabase
          .from('trail_segments')
          .select(`
            id,
            trail_id,
            trail_name,
            segment_order,
            length_miles,
            difficulty,
            surface_type,
            features,
            mvum_symbol,
            maintenance_level,
            allowed_vehicles,
            forest_name,
            district_name,
            jurisdiction,
            existence_status,
            seasonal_restrictions
          `);

        // If bbox is provided, filter by geography (you'll need PostGIS functions)
        if (bbox) {
          // For now, just limit results
          query = query.limit(10000);
        } else {
          query = query.limit(1000);
        }

        // Use optimized RPC function to get real PostGIS geometry with bbox filtering
        const { data: rpcData, error: rpcError } = await supabase
          .rpc('get_trail_segments', {
            bbox_west: bbox?.west,
            bbox_south: bbox?.south,
            bbox_east: bbox?.east,
            bbox_north: bbox?.north,
            zoom_level: bbox?.zoom || 10,
            max_limit: bbox ? 10000 : 1000
          });

        if (!rpcError && rpcData) {
          setSegments(rpcData);
          return;
        }

        // Fallback: get segments without geometry, then add placeholder
        const { data, error: fetchError } = await query;

        if (fetchError) {
          throw fetchError;
        }

        // Add placeholder geometry for segments that don't have it
        const segmentsWithGeometry = (data || []).map((segment, index) => ({
          ...segment,
          geometry: {
            type: 'LineString' as const,
            coordinates: [
              [-116.891 - (index * 0.001), 34.082 + (index * 0.001)], 
              [-116.890 - (index * 0.001), 34.083 + (index * 0.001)]
            ]
          }
        }));

        setSegments(segmentsWithGeometry);
      } catch (err) {
        console.error('Error fetching trail segments:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch trail segments');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSegments();
  }, [bbox?.north, bbox?.south, bbox?.east, bbox?.west, bbox?.zoom]);

  return { segments, isLoading, error };
};